import { PropsWithChildren } from 'react'
import { useLocation } from 'react-router-dom'
import { WellMode } from '../../../types/routing-types'
import { Permissions } from '../../../types/user-types'
import useUser from '../../hooks/use-user'
import { can } from '../../services/user-utils'
import { isValidUser } from '../../services/user-utils'
import { Button } from '@msaf/core-react'

interface WellViewButtonsInterface {
  label?: string
  continueRoute?: string
  wellId: number | string
}

/**
 * Renders buttons for forms in view mode
 *
 * Note: `continueRoute` prop is unused at the moment
 * but this will allow us to add a 'Continue' button in view mode for better UX
 */
export default function WellViewButtons({
  label = 'Edit record',
  continueRoute,
  wellId,
}: PropsWithChildren<WellViewButtonsInterface>) {
  const { user } = useUser()
  const location = useLocation()
  const toggleLink = location.pathname.replace(WellMode.VIEW, WellMode.EDIT)

  if (!isValidUser(user) || !user) {
    return continueRoute ? <Button label='Continue' path={continueRoute} /> : <></>
  }

  /**
   * Details for which button to show:
   * - If the user can never edit any well, never show any edit button - disabled or otherwise
   * - If the user can edit _some_ wells, and they can edit this well - show button enabled
   * - If the user can edit _some_ wells, but not this well - show button disabled
   */
  let button = <></>
  if (can(user, Permissions.EDIT_WELL_UNSCOPED)) {
    // Can always edit, so show button
    button = <Button label={label} path={toggleLink} />
  } else if (can(user, Permissions.EDIT_WELL)) {
    // User can edit some wells, can they edit this well?
    if (user.objectPermissions?.canEdit) {
      button = <Button label={label} path={toggleLink} isDisabled={!user.objectPermissions?.canEdit} />
    } else if (continueRoute) {
      button = <Button label='Continue' path={continueRoute} />
    }
  }

  return <>{button}</>
}
